import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/** Serialized shape of a brand example as the API returns it. */
export interface BrandExampleItem {
  id: string;
  notes: string | null;
  disabled: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  /** Short-lived presigned view URL. */
  imageUrl: string;
}

const QUERY_KEY = ['brand-examples'];

export const useBrandExamples = () => {
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiClient.get<{ items: BrandExampleItem[] }>('brand-examples'),
    staleTime: 60 * 1000,
  });
  return {
    examples: query.data?.items ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useCreateBrandExample = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: { imageBase64: string; notes?: string }) =>
      apiClient.post('brand-examples', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Example added');
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Failed to add example'),
  });
  return { createExample: mutation.mutate, isCreating: mutation.isPending };
};

export const useSetBrandExampleDisabled = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      apiClient.patch(`brand-examples/${id}`, { disabled }),
    onSuccess: (_, { disabled }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(disabled ? 'Example disabled' : 'Example enabled');
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Failed to update example'),
  });
  return { setDisabled: mutation.mutate, isUpdating: mutation.isPending };
};

export const useDeleteBrandExample = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`brand-examples/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Example deleted');
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Failed to delete example'),
  });
  return { deleteExample: mutation.mutate, isDeleting: mutation.isPending };
};
